import { useCallback, useMemo } from 'react'

import {
  SkeletonBox,
  TableFilters,
  useTableFilters,
} from '@circlefin/components'
import { getBlockchain } from '@services/static-data/blockchains'
import { BlockchainActivityType, useBlockchainsQuery } from '@shared/graphql'
import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'

import type { SelectListItem } from '@circlefin/components/lib/SelectList'
import type { FilterDropdownProps } from '@circlefin/components/lib/TableFilters/Dropdown/Dropdown'
import type { BlockchainAbbreviation } from '@shared/graphql'

export type BlockchainProps = Omit<
  FilterDropdownProps,
  'items' | 'placeholder' | 'name'
>

/**
 * Search by blockchain.
 */
export const Blockchain: React.FC<BlockchainProps> = (props) => {
  const { t } = useTranslation('address-book/filters')

  const { data, loading } = useBlockchainsQuery({
    variables: {
      input: {
        activityType: BlockchainActivityType.ALL,
      },
    },
  })
  const { setFilter } = useTableFilters()

  const items: Array<SelectListItem<BlockchainAbbreviation>> = useMemo(() => {
    return [
      {
        value: undefined,
        label: t('table.blockchain.all'),
      },
      ...(data?.blockchains.map(({ chain }) => ({
        label: getBlockchain(chain)?.name ?? '',
        value: chain,
      })) ?? []),
    ]
  }, [t, data?.blockchains])

  const resetAssets = useCallback(
    () => setFilter('asset', undefined),
    [setFilter],
  )

  return (
    <SkeletonBox className={classNames('w-full')} loading={loading}>
      <TableFilters.Dropdown
        {...props}
        items={items}
        name="blockchain"
        onChange={resetAssets}
        placeholder={t`table.blockchain.label`}
      />
    </SkeletonBox>
  )
}
